// Sidebar category edits (shared/API.md §41.1): move a conversation, create / rename / delete /
// reorder categories. PURE — the worker applies the result to the store first (local-first) and
// sends the same shapes to the server; the server's answer (and its `sidebar_category_*` event
// refetch) is the truth afterwards.
//
// Server facts read from Mattermost 12 (`server/channels/store/sqlstore/channel_store_categories.go`):
// - A channel lives in exactly ONE category per team; moving = remove it from the source's
//   `channel_ids`, append it to the target's, PUT both in one call.
// - The `direct_messages` category does not store its channels: it is "every DM/GM not placed in
//   another category". Its `channel_ids` in a PUT are ignored, so moving a DM back there = taking it
//   out of the custom/favourites category it was in.
// - Only `custom` categories may be renamed or deleted; deleting returns their channels to the
//   default categories (O/P → `channels`, D/G → `direct_messages`).
// - A new category goes right after Favorites when Favorites is first, else first.
// - The webapp offers a channel every category but Direct Messages, and a DM/GM every category but
//   Channels; the web follows that (the server would accept either, the sidebar would not show it).
// - The webapp caps a category name at 22 characters (`MAX_CATEGORY_NAME_LENGTH`).

export interface CategoryEntry {
  id: string;
  /** "favorites" | "channels" | "direct_messages" | "custom" */
  type: string;
  displayName: string;
  channelIds: string[];
}

export const maxNameLength = 22;

export function isConversation(channelType: string): boolean {
  return channelType === 'D' || channelType === 'G';
}

export function canRename(type: string): boolean {
  return type === 'custom';
}

export function canDelete(type: string): boolean {
  return type === 'custom';
}

/** The trimmed name, or null when it cannot be saved (empty or too long). */
export function validName(raw: string): string | null {
  const name = raw.trim();
  if (!name || [...name].length > maxNameLength) return null;
  return name;
}

/** The category holding the channel (first match), or null. */
export function categoryOf(entries: readonly CategoryEntry[], channelId: string): CategoryEntry | null {
  return entries.find((c) => c.channelIds.includes(channelId)) ?? null;
}

/**
 * Where «Mover a…» may send the conversation: every category but its own, minus Direct Messages for
 * a channel and minus Channels for a DM/GM. A DM not placed anywhere is IN Direct Messages.
 */
export function moveTargets(entries: readonly CategoryEntry[], channelId: string, channelType: string): CategoryEntry[] {
  const dm = isConversation(channelType);
  let current = categoryOf(entries, channelId)?.id ?? null;
  if (!current && dm) current = entries.find((c) => c.type === 'direct_messages')?.id ?? null;
  return entries.filter((c) => c.id !== current && !(dm ? c.type === 'channels' : c.type === 'direct_messages'));
}

/**
 * The whole changed categories for a move (source and target), or [] when nothing moves. Appends at
 * the end of the target (§41.1: "moving into a manual category appends at the end").
 */
export function moveChannel(entries: readonly CategoryEntry[], channelId: string, targetId: string): CategoryEntry[] {
  const target = entries.find((c) => c.id === targetId);
  if (!target || target.channelIds.includes(channelId)) return [];
  const changed: CategoryEntry[] = [];
  for (const c of entries) {
    if (c.id !== targetId && c.channelIds.includes(channelId)) changed.push({ ...c, channelIds: c.channelIds.filter((id) => id !== channelId) });
  }
  changed.push({ ...target, channelIds: [...target.channelIds.filter((id) => id !== channelId), channelId] });
  return changed;
}

/** `entries` with the changed categories swapped in (same order). */
export function applyChanged(entries: readonly CategoryEntry[], changed: readonly CategoryEntry[]): CategoryEntry[] {
  const byId = new Map(changed.map((c) => [c.id, c]));
  return entries.map((c) => byId.get(c.id) ?? c);
}

/** The renamed category, or null (not custom, unknown, invalid or unchanged name). */
export function rename(entries: readonly CategoryEntry[], categoryId: string, raw: string): CategoryEntry | null {
  const c = entries.find((x) => x.id === categoryId);
  const name = validName(raw);
  if (!c || !canRename(c.type) || !name || name === c.displayName) return null;
  return { ...c, displayName: name };
}

/**
 * The local picture after deleting a custom category: it is gone and its channels went back to the
 * default categories (channels → Channels, conversations → Direct Messages), appended. null when it
 * cannot be deleted.
 */
export function deleteCategory(entries: readonly CategoryEntry[], categoryId: string, channelTypes: ReadonlyMap<string, string>): CategoryEntry[] | null {
  const c = entries.find((x) => x.id === categoryId);
  if (!c || !canDelete(c.type)) return null;
  const toChannels = c.channelIds.filter((id) => !isConversation(channelTypes.get(id) ?? 'O'));
  const toDms = c.channelIds.filter((id) => isConversation(channelTypes.get(id) ?? 'O'));
  return entries
    .filter((x) => x.id !== categoryId)
    .map((x) => {
      if (x.type === 'channels' && toChannels.length) return { ...x, channelIds: [...x.channelIds, ...toChannels.filter((id) => !x.channelIds.includes(id))] };
      if (x.type === 'direct_messages' && toDms.length) return { ...x, channelIds: [...x.channelIds, ...toDms.filter((id) => !x.channelIds.includes(id))] };
      return x;
    });
}

/**
 * Inserts a new category where the server puts it (after Favorites when Favorites is first, else
 * first), moving `channelIds` out of wherever they were.
 */
export function insertNew(entries: readonly CategoryEntry[], created: CategoryEntry): CategoryEntry[] {
  const moving = new Set(created.channelIds);
  const rest = entries.map((c) => (c.channelIds.some((id) => moving.has(id)) ? { ...c, channelIds: c.channelIds.filter((id) => !moving.has(id)) } : c));
  const at = rest[0]?.type === 'favorites' ? 1 : 0;
  return [...rest.slice(0, at), created, ...rest.slice(at)];
}

/** The new full order after moving one category up or down, or null at an edge / unknown id. */
export function reorder(order: readonly string[], categoryId: string, direction: 'up' | 'down'): string[] | null {
  const i = order.indexOf(categoryId);
  if (i < 0) return null;
  const j = direction === 'up' ? i - 1 : i + 1;
  if (j < 0 || j >= order.length) return null;
  const next = [...order];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/** Whether «Subir» / «Bajar» are offered for this category. */
export function canMove(order: readonly string[], categoryId: string, direction: 'up' | 'down'): boolean {
  return reorder(order, categoryId, direction) !== null;
}

export const CategoryEdits = {
  maxNameLength,
  isConversation,
  canRename,
  canDelete,
  validName,
  categoryOf,
  moveTargets,
  moveChannel,
  applyChanged,
  rename,
  deleteCategory,
  insertNew,
  reorder,
  canMove,
} as const;
